import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UserInitModule } from '@features/user-init/user-init.module';
import { UserPreferenceModule } from '@features/user-preference/user-preference.module';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AppInitModule } from '@rd/app-init/app-init.module';
import { BroadcastModule } from '@rd/broadcast/broadcast.module';
import { AssetServiceModule } from '@rd/core/services/asset-service/asset.service.module';
import { PopupModule } from '@rd/popup/popup.module';
import { ButtonModule } from '@rd/shared-legacy/components/button/button.module';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { LinkModule } from '@rd/shared-legacy/components/link/link.module';
import { LocalizeNumberPipeModule } from '@rd/shared-legacy/pipes/localize-number/localize-number.pipe';
import { AuthGuard } from './auth/guards/auth.guard';
import { AuthEffects } from './auth/store/effects/auth.effects';
import { AuthErrorMessageDialogComponent } from './components/auth-error-message-dialog.component/auth-error-message-dialog.component';
import { AuthSecureRouteDialogComponent } from './components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { BypassBlockWrapperComponent } from './components/bypass-block/bypass-block-wrapper.component';
import { BypassBlockComponent } from './components/bypass-block/bypass-block.component';
import { FooterWrapperComponent } from './components/footer/footer-wrapper.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderDropdownLinkComponent } from './components/header-dropdown-link/header-dropdown-link.component';
import { HeaderLocaleSelectMobileComponent } from './components/header-locale-select-mobile/header-locale-select-mobile.component';
import { SideNavLinkComponent } from './components/side-nav-link/side-nav-link.component';
import { SideNavWrapperComponent } from './components/side-nav/side-nav-wrapper.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { AuthSessionExpiredDialogComponent } from './containers/auth-session-expired-dialog/auth-session-expired-dialog.component';
import { AuthUserIdleDialogComponent } from './containers/auth-user-idle-dialog/auth-user-idle-dialog.component';
import { HeaderWrapperComponent } from './containers/header/header-wrapper.component';
import { HeaderComponent } from './containers/header/header.component';
import { HomePageComponent } from './containers/home-page/home-page.component';
import { CurrencyEffects } from './currency/store/effects/currency.effects';
import { RdAppLoadingOnClickDirective } from './directives/app-loading-onclick.directive';
import { LocaleFacade } from './facades/locale-facade/locale.facade';
import { PointCashFacade } from './facades/point-cash-facade/point-cash.facade';
import { UserCustomizationsFacade } from './facades/user-customizations-facade/user-customizations.facade';
import { LocaleEffects } from './locale/store/effects/locale.effects';
import { AnalyticsService } from './services/analytics/analytics.service';
import { ScrollRestorationService } from './services/scroll-restoration/scroll-restoration.service';
import { reducers } from './store/reducers/index';
import { TenantEffects } from './tenant/store/effects/tenant.effects';
import {
  RD_PLATFORM_MAINTENANCE_ENABLED,
  RD_PLATFORM_MAINTENANCE_UNLEASH_KEY,
} from './tokens/rd-lib.token';
import { UnleashInitEffects } from './unleash-init/unleash-init.effects';
import { UserCustomizationsEffects } from './user-customizations/store/effects/user-customizations.effects';
import { PageNotFoundComponent } from './views/page-not-found/page-not-found.component';

const declaredComponents: any[] = [
  AuthSecureRouteDialogComponent,
  AuthSessionExpiredDialogComponent,
  AuthUserIdleDialogComponent,
  AuthErrorMessageDialogComponent,
  BypassBlockComponent,
  BypassBlockWrapperComponent,
  FooterComponent,
  FooterWrapperComponent,
  HeaderComponent,
  HeaderDropdownLinkComponent,
  HeaderLocaleSelectMobileComponent,
  HeaderWrapperComponent,
  HomePageComponent,
  SideNavComponent,
  SideNavLinkComponent,
  SideNavWrapperComponent,
  PageNotFoundComponent,
];

const entryComponents: any[] = [
  AuthSecureRouteDialogComponent,
  AuthSessionExpiredDialogComponent,
  AuthUserIdleDialogComponent,
  FooterComponent,
  HeaderComponent,
  SideNavComponent,
];

@NgModule({
  declarations: [...declaredComponents, ...entryComponents],
  imports: [
    AssetServiceModule,
    BroadcastModule,
    AppInitModule,
    ButtonModule,
    CommonModule,
    FormsModule,
    IconModule,
    LinkModule,
    LocalizeNumberPipeModule,
    UserInitModule,
    StoreModule.forFeature('core', reducers),
    EffectsModule.forFeature([
      AuthEffects,
      CurrencyEffects,
      LocaleEffects,
      TenantEffects,
      UnleashInitEffects,
      UserCustomizationsEffects,
    ]),
    TranslateModule.forChild(),
    RouterModule,
    PopupModule,
    UserPreferenceModule,
    RdAppLoadingOnClickDirective,
  ],
  exports: [...declaredComponents],
  providers: [
    AuthGuard,
    AnalyticsService,
    LocaleFacade,
    UserCustomizationsFacade,
    ScrollRestorationService,
    PointCashFacade,
    {
      provide: RD_PLATFORM_MAINTENANCE_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(RD_PLATFORM_MAINTENANCE_UNLEASH_KEY),
      deps: [UnleashService],
    },
  ],
})
export class CoreModule {}
